import styled, { css } from "styled-components";
import { FC } from "react";
import { components } from "react-select";

import { useDropdownContext } from "./DropdownContext";
import { SelectComponentProps } from "./types";
import { styleWrappedComponent } from "./styleWrappedComponent";
import { IconVariantArgs, LabelWithIcon } from "./LabelWithIcon";
import { Icon, IconName, IconSize } from "../../Icon";
import { alpha } from "../../../typography";

type StyledOptionProps = {
  $disabled: boolean;
  $selected: boolean;
  $focused: boolean;
};

const StyledOption = styled.div<StyledOptionProps>`
  ${alpha.primaryActive.body3};
  ${styleWrappedComponent(css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    white-space: pre-line;
  `)};
  ${({ $disabled }) =>
    $disabled &&
    styleWrappedComponent(css`
      ${alpha.primaryDisabled.body3}
      cursor: not-allowed;
    `)}
  ${({ $selected }) =>
    $selected &&
    styleWrappedComponent(css`
      ${alpha.primaryActive.body2};
      color: var(--color-secondary-800);
      background-color: var(--color-neutral-000-full);
    `)}
    ${({ $focused }) =>
    $focused &&
    styleWrappedComponent(
      css`
        background-color: var(--color-secondary-200);
      `
    )}
`;

type OptionProps = SelectComponentProps["Option"] & {
  data: {
    id?: string;
    value: string;
    label: string;
    disabled: boolean;
    iconName?: IconName;
    iconSize?: IconSize;
  };
};

export const Option: FC<OptionProps> = ({ children, ...props }) => {
  const { allowIcons } = useDropdownContext();

  const {
    isDisabled,
    isSelected,
    innerProps,
    isFocused,
    data,
    selectProps: { translateOptions = true },
  } = props;
  const { iconName, iconSize, id } = data;

  const iconVariantArgs: IconVariantArgs = {
    isSelected,
    isDisabled,
  };
  const originalOnClick = innerProps.onClick;
  const newOnClick: typeof originalOnClick = (e) => {
    const canBeClicked = !isSelected && !isDisabled;
    if (canBeClicked) {
      originalOnClick(e);
    }
  };

  innerProps.onClick = newOnClick;

  const dataset = {
    "data-testid": "Option",
    ...(translateOptions ? {} : { "data-notranslate": "true" }),
  };

  return (
    <StyledOption
      id={id}
      $disabled={isDisabled}
      $selected={isSelected}
      $focused={isFocused}
      {...dataset}
    >
      <components.Option {...props}>
        <LabelWithIcon
          iconName={iconName}
          iconSize={iconSize}
          allowIcons={allowIcons}
          IconVariantArgs={iconVariantArgs}
        >
          {children}
        </LabelWithIcon>
        {isSelected && (
          <Icon iconName="checkmark" iconSize="small" variant="ACTIVE" plain />
        )}
      </components.Option>
    </StyledOption>
  );
};
